"use client";

import React, { memo, useCallback, useMemo } from "react";
import { Button, Slider, Box, Text, VStack, Checkbox } from "@chakra-ui/react";
import GenericSelector from "@/shared/ui/controls/GenericSelector";
import { useOptionItems } from "@/shared/ui/controls/useOptionItems";

interface SearchLanguageRefinementsProps {
  selectedLanguages: string[];
  onLanguageToggle: (language: string) => void;
  title?: string;
}

export const SearchLanguageRefinements = memo(function SearchLanguageRefinements({
  selectedLanguages,
  onLanguageToggle,
  title,
}: SearchLanguageRefinementsProps) {
  const { languageItems } = useOptionItems();

  return (
    <Box>
      {title && (
        <Text fontSize="sm" mb="2" fontWeight="medium">
          {title}
        </Text>
      )}
      <VStack align="stretch" gap="2" maxH="200px" overflowY="auto">
        {languageItems.map(({ value, label }) => (
          <Checkbox.Root
            key={value}
            checked={selectedLanguages.includes(value)}
            onCheckedChange={() => onLanguageToggle(value)}
            size="sm"
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control
              _checked={{
                bg: "moveon.secondaryLight",
                borderColor: "moveon.secondary",
              }}
            />
            <Checkbox.Label>
              <Text fontSize="sm">{label}</Text>
            </Checkbox.Label>
          </Checkbox.Root>
        ))}
      </VStack>
    </Box>
  );
});

export const SearchClearLanguageFiltersButton = memo(
  function SearchClearLanguageFiltersButton({
    onClear,
    hasFilters,
  }: {
    onClear: () => void;
    hasFilters: boolean;
  }) {
    return (
      <Button
        size="sm"
        w="full"
        borderRadius="md"
        onClick={onClear}
        disabled={!hasFilters}
        bg="moveon.secondaryLight"
      >
        Clear language filters
      </Button>
    );
  },
);

export const SearchHitsPerPageSelector = memo(function SearchHitsPerPageSelector({
  value,
  onChange,
  items,
}: {
  value: number;
  onChange: (value: number) => void;
  items: { value: number; label: string }[];
}) {
  const available = useMemo(() => {
    return items.map((item) => ({
      value: String(item.value),
      label: item.label,
    }));
  }, [items]);

  const selected = useMemo(() => String(value), [value]);

  const handleSelect = useCallback(
    (vals: string[]) => {
      if (vals.length > 0) {
        const parsed = parseInt(vals[0], 10);
        if (!Number.isNaN(parsed)) onChange(parsed);
      }
    },
    [onChange],
  );

  return (
    <GenericSelector
      previewItems={available}
      stateObject={[selected]}
      stateObjectSetter={handleSelect}
      labelText="Hits Per Page"
      mode="select"
      placeholder="Hits Per Page"
      disableClear={true}
    />
  );
});

interface SearchSemanticRatioSliderProps {
  stateObject: number[];
  stateObjectSetter: (vals: number[]) => void;
  min?: number;
  max?: number;
  step?: number;
  title?: string;
  isDecimal?: boolean;
}

export const SearchSemanticRatioSlider = memo(function SearchSemanticRatioSlider({
  stateObject,
  stateObjectSetter,
  min = 0,
  max = 100,
  step = 10,
  title,
  isDecimal = false,
}: SearchSemanticRatioSliderProps) {
  const displayValues = useMemo(
    () => (isDecimal ? stateObject.map((val) => val * 100) : stateObject),
    [stateObject, isDecimal],
  );

  const handleValueChange = useCallback(
    (vals: { value: number[] }) => {
      const convertedValues = isDecimal
        ? vals.value.map((val) => val / 100)
        : vals.value;
      stateObjectSetter(convertedValues);
    },
    [isDecimal, stateObjectSetter],
  );

  const marks = useMemo(
    () =>
      Array.from(
        { length: Math.floor((max - min) / step) + 1 },
        (_, i) => min + i * step,
      ),
    [min, max, step],
  );

  return (
    <Slider.Root
      size="md"
      value={displayValues}
      min={min}
      max={max}
      step={step}
      onValueChange={handleValueChange}
    >
      <Slider.Label>{title}</Slider.Label>
      <Slider.Control>
        <Slider.Track bg="moveon.inactive">
          <Slider.Range bg="moveon.secondaryLight" />
        </Slider.Track>
        <Slider.Thumbs />
        <Slider.Marks marks={marks} />
      </Slider.Control>
    </Slider.Root>
  );
});
